import React from "react";
import { cn } from "@/lib/utils";

/**
 * PageHeader — the single, canonical way every page in the app announces
 * itself and hosts its top-level toolbar. HeroUI-inspired: soft-surface
 * card, generous rhythm, and a responsive layout that scales cleanly from
 * phone to desktop:
 *
 *   PHONE  (default)  → title stacked; actions in a 2-col grid, filters full-width
 *   TABLET (sm-md)    → title + subtitle on top; actions row directly under, wraps
 *   DESKTOP (lg+)     → title on left, actions row on the right, single line
 *
 * Every control in `actions` (Buttons, Selects, Pickers) inherits `h-9` from
 * the shared component set, so they line up perfectly by default.
 *
 * Usage:
 *   <PageHeader
 *     title={t("sales_title")}
 *     subtitle={t("sales_subtitle")}
 *     meta={<UpdatedThroughChip date={meta.updated_through} />}
 *     primaryAction={<Button ...>Import</Button>}
 *     actions={
 *       <>
 *         <PeriodPicker ... />
 *         <BranchFilter ... />
 *       </>
 *     }
 *   />
 */
export function PageHeader({
    title,
    subtitle,
    meta,           // optional inline meta chip / timestamp — sits beside subtitle
    icon: Icon,     // optional lucide icon component to render before the title
    primaryAction,  // the ONE main CTA (Import, Add, Start Round)
    actions,        // Right-side filters / secondary actions (children)
    className,
    testId,
}) {
    const hasRightSide = !!(primaryAction || actions);
    return (
        <header
            data-testid={testId}
            className={cn(
                "flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between",
                className,
            )}
        >
            {/* -------- Title block -------- */}
            <div className="flex items-start gap-3 min-w-0 lg:min-w-[220px]">
                {Icon ? (
                    <span className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-sm)] bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-brown)] shrink-0">
                        <Icon className="h-5 w-5" />
                    </span>
                ) : null}
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-extrabold text-[color:var(--ink)] leading-tight">
                        {title}
                    </h1>
                    {subtitle || meta ? (
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-[color:var(--ink-muted)]">
                            {subtitle ? <span>{subtitle}</span> : null}
                            {subtitle && meta ? <span aria-hidden="true">·</span> : null}
                            {meta}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* -------- Toolbar (actions row) --------
               Desktop: single row, wrap-safe.
               Tablet: 2-col grid — every action is at least half the row.
               Phone: 1-col grid — every action is full width for thumb reach. */}
            {hasRightSide ? (
                <div
                    className={cn(
                        "grid gap-2",
                        "grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center lg:justify-end",
                        "w-full lg:w-auto lg:shrink-0",
                    )}
                >
                    {primaryAction ? (
                        <div className="sm:col-span-2 lg:col-auto lg:order-last">
                            {/* On mobile+tablet the primary CTA spans full width and sits ABOVE the filters
                               so the most important tap-target is on top. On desktop it slides to the right
                               end of the toolbar (order-last). */}
                            {React.cloneElement(primaryAction, {
                                className: cn(
                                    primaryAction.props.className,
                                    "w-full lg:w-auto",
                                ),
                            })}
                        </div>
                    ) : null}
                    {actions}
                </div>
            ) : null}
        </header>
    );
}

/**
 * Toolbar — a lighter-weight bar for pages that need a filter row separately
 * from the page title (e.g. a dashboard whose filters live below its KPI strip).
 * Same responsive rules as PageHeader's actions block.
 */
export function Toolbar({ children, className, testId }) {
    return (
        <div
            data-testid={testId}
            className={cn(
                "grid gap-2",
                "grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center",
                className,
            )}
        >
            {children}
        </div>
    );
}

/**
 * UpdatedThroughChip — the "· Updated through Jun 24, 2026" tail that used
 * to live inline in the subtitle. Now a proper pill so it doesn't get
 * swallowed when the subtitle is long.
 */
export function UpdatedThroughChip({ label, value }) {
    if (!value) return null;
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--bg-surface-2)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--ink-muted)]">
            {label ? <span>{label}</span> : null}
            <bdi dir="ltr" className="font-semibold text-[color:var(--ink)]">
                {value}
            </bdi>
        </span>
    );
}
